import type { TemplateCampaign } from '../stores/gemX';

import useLocationHandlers from '@/composables/useLocationHandlers';
import getParameterByName from '@/app/utils/getParameterByName';
import { getTemplateSuffix } from '../utils/template';
import { fetchPlatformGemX, fetchTemplateCampaign, isConnectedGemX, templateCampaign } from '../stores/gemX';

const GEMX_INSTALL_URL = 'https://apps.shopify.com/gemx-cro';

/**
 * GemX admin routes. A campaign covering a single template has its own screen, one belonging to a
 * group (a bundle, i.e. multipage experiment) is only reachable through its group.
 */
const GEMX_ROUTE_PATH = {
  CAMPAIGNS: 'campaign',
  CAMPAIGN_NEW: 'campaign/new',
  CAMPAIGN_TEMPLATE: 'campaign',
  CAMPAIGN_MULTIPAGE: 'campaign/multipage',
} as const;

/**
 * Where GemX shows an existing experiment, mirroring `useGemXCampaignNavigate` in the V7 app: a
 * bundle campaign only has a screen through its group (multipage experiment), a single-template
 * campaign has its own, and anything else can only fall back to the campaign list.
 */
const getExistingCampaignPath = (campaign: TemplateCampaign): string => {
  switch (campaign.type) {
    case 'TEMPLATE_BUNDLE': {
      const groupId = campaign.group?.id;

      return groupId
        ? `${GEMX_ROUTE_PATH.CAMPAIGN_MULTIPAGE}/${encodeURIComponent(groupId)}/detail`
        : GEMX_ROUTE_PATH.CAMPAIGNS;
    }

    case 'TEMPLATE':
      return `${GEMX_ROUTE_PATH.CAMPAIGN_TEMPLATE}/${encodeURIComponent(campaign.id)}/detail`;

    default:
      return GEMX_ROUTE_PATH.CAMPAIGNS;
  }
};

export const useAbTestingRedirect = (pageType: string) => {
  const { getGemXAdminUrl } = useLocationHandlers();

  // This runs outside `setup()` too (the publish tooltip is built from jQuery), so it only reads
  // prefetched state instead of calling composables. The fetch here just covers the case where
  // shopInfo lands after the first render.
  fetchPlatformGemX();
  fetchTemplateCampaign();

  const idPageTemplate = getParameterByName('id') || '';
  const isConnected = isConnectedGemX.value;
  const campaign = templateCampaign.value;
  // This template is already being A/B tested, whether or not the experiment has started: the tip
  // keeps its wording and only sends the user to that experiment instead of offering a new one.
  const hasCampaign = isConnected && !!campaign?.id;

  let redirectUrl = GEMX_INSTALL_URL;
  if (hasCampaign && campaign) {
    redirectUrl = getGemXAdminUrl(getExistingCampaignPath(campaign));
  } else if (isConnected) {
    redirectUrl = getGemXAdminUrl(
      `${GEMX_ROUTE_PATH.CAMPAIGN_NEW}?templateSuffix=${getTemplateSuffix(pageType, idPageTemplate)}`,
    );
  }

  let buttonLabel = 'Install GemX to create A/B test';
  if (hasCampaign) {
    buttonLabel = 'Go to GemX';
  } else if (isConnected) {
    buttonLabel = 'Create A/B Testing with this page';
  }

  return {
    redirectUrl,
    buttonLabel,
    isConnected,
    hasCampaign,
    isCampaignGroup: hasCampaign && campaign?.type === 'TEMPLATE_BUNDLE',
  };
};
